import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { IMAGES, HERO_VIDEO, HERO_VIDEO_WEBM } from "@/lib/media";
import { scrollToId } from "@/hooks/useLenis";

const EASE = [0.76, 0, 0.24, 1];

const line = (i) => ({
  initial: { y: "115%" },
  animate: { y: "0%" },
  transition: { duration: 1, ease: EASE, delay: 0.4 + i * 0.12 },
});

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-40%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section id="hero" ref={ref} className="relative h-screen w-full overflow-hidden">
      {/* Background media with parallax (video if provided, else photo) */}
      <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0">
        {HERO_VIDEO ? (
          <video
            data-testid="hero-video"
            autoPlay
            muted
            loop
            playsInline
            /* Het intro-scherm haalt deze video al binnen (met voortgangsbalk),
               dus hij komt hier uit de cache. Blijft "metadata" staan als
               vangnet: komt iemand met een bestaande sessie direct binnen,
               dan streamt hij gewoon achter de poster in plaats van eerst
               10,4 MB te eisen. */
            preload="metadata"
            poster={IMAGES.heroDaniq}
            className="h-full w-full object-cover object-center"
          >
            {/* mp4 eerst: de webm-variant is met 13,8 MB juist groter */}
            <source src={HERO_VIDEO} type="video/mp4" />
            <source src={HERO_VIDEO_WEBM} type="video/webm" />
          </video>
        ) : (
          <img src={IMAGES.heroDaniq} alt="DaniQ performing" className="h-full w-full object-cover object-center animate-[kenburns_18s_ease-in-out_infinite_alternate]" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-ink-950 via-ink-950/40 to-ink-950/70" />
        <div className="absolute inset-0 bg-ink-950/30" />
      </motion.div>

      {/* Beat-reactive glow */}
      <div className="pointer-events-none absolute -bottom-40 left-1/2 -translate-x-1/2 h-[500px] w-[500px] rounded-full bg-magenta/25 blur-[130px] animate-pulse-glow" />
      <div className="pointer-events-none absolute top-1/4 -right-20 h-[360px] w-[360px] rounded-full bg-violet/25 blur-[120px]" />

      {/* Content */}
      <motion.div style={{ y: textY, opacity: fade }} className="relative z-10 flex h-full flex-col justify-center px-6 lg:px-16 max-w-[1400px] mx-auto">
        <motion.p
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3, duration: 1 }}
          className="mb-5 text-xs sm:text-sm uppercase tracking-[0.5em] text-gold"
        >
          DJ · Drummer · MC · Producer
        </motion.p>

        <h1 className="font-display leading-[0.82] text-white">
          <span className="reveal-mask block">
            <motion.span {...line(0)} className="block text-[22vw] sm:text-[18vw] lg:text-[15vw] tracking-[0.02em]">
              DANI<span className="text-magenta">Q</span>
            </motion.span>
          </span>
          <span className="reveal-mask block mt-1">
            <motion.span {...line(1)} className="block text-3xl sm:text-5xl lg:text-6xl tracking-[0.35em] text-white/85">
              DJ ON DRUMS
            </motion.span>
          </span>
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.4, duration: 0.8, ease: EASE }}
          className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-5"
        >
          <button
            data-testid="hero-cta-feel-the-beat"
            onClick={() => scrollToId("about")}
            className="group relative overflow-hidden rounded-full bg-magenta px-9 py-4 text-sm font-medium uppercase tracking-[0.2em] text-white glow-magenta transition-transform duration-300 hover:scale-[1.03]"
          >
            <span className="relative z-10">Feel the Beat</span>
            <span className="absolute inset-0 -translate-x-full bg-violet transition-transform duration-500 group-hover:translate-x-0" />
          </button>
          <button
            data-testid="hero-cta-booking"
            onClick={() => scrollToId("booking")}
            className="rounded-full border border-white/30 px-9 py-4 text-sm uppercase tracking-[0.2em] text-white/90 hover:border-white hover:bg-white/5 transition-colors duration-300"
          >
            Book DaniQ
          </button>
        </motion.div>
      </motion.div>

      {/* Scroll cue */}
      <motion.div style={{ opacity: fade }} className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2">
        <span className="text-[10px] uppercase tracking-[0.3em] text-white/50">Scroll</span>
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.6 }}>
          <ArrowDown className="h-4 w-4 text-magenta" />
        </motion.div>
      </motion.div>
    </section>
  );
};
